import { nextAvailableName } from "./nextAvailableName.ts";
import { parseNum } from "./parseNum.ts";

export interface Pollutant {
  name: string;
  units: string;
  rainConc: number;
  gwConc: number;
  iiConc: number;
  decayCoeff: number;
  snowOnly: string;
  /** Another pollutant's own name, or "*" for none. */
  coPollutant: string;
  coFraction: number;
  dwfConc: number;
  initConc: number;
}

export function readPollutants(
  raw: ReadonlyMap<string, string[][]>,
  warnings?: Set<string>
): Pollutant[] {
  return (raw.get("POLLUTANTS") ?? [])
    .filter((row) => row[0])
    .map((row) => {
      const ctx = (field: string) => `[POLLUTANTS] "${row[0]}" ${field}`;
      return {
        name: row[0],
        units: row[1] ?? "MG/L",
        rainConc: parseNum(row[2], 0, ctx("Rain Conc."), warnings),
        gwConc: parseNum(row[3], 0, ctx("GW Conc."), warnings),
        iiConc: parseNum(row[4], 0, ctx("I&I Conc."), warnings),
        decayCoeff: parseNum(row[5], 0, ctx("Decay Coeff."), warnings),
        snowOnly: row[6] ?? "NO",
        coPollutant: row[7] ?? "*",
        coFraction: parseNum(row[8], 0, ctx("Co-Fraction"), warnings),
        dwfConc: parseNum(row[9], 0, ctx("DWF Conc."), warnings),
        initConc: parseNum(row[10], 0, ctx("Init. Conc."), warnings),
      };
    });
}

/** Always writes all 10 columns, unlike LandUses' own optional trailing ones. */
export function writePollutants(
  raw: ReadonlyMap<string, string[][]>,
  pollutants: Pollutant[]
): Map<string, string[][]> {
  const rows = pollutants.map((p) => [
    p.name,
    p.units,
    String(p.rainConc),
    String(p.gwConc),
    String(p.iiConc),
    String(p.decayCoeff),
    p.snowOnly,
    p.coPollutant,
    String(p.coFraction),
    String(p.dwfConc),
    String(p.initConc),
  ]);
  const next = new Map(raw);
  next.set("POLLUTANTS", rows);
  return next;
}

export const POLLUTANT_UNITS = ["MG/L", "UG/L", "#/L"] as const;

export function nextAvailablePollutantName(existingNames: Set<string>): string {
  return nextAvailableName(existingNames, "POL");
}
